import { Injectable } from '@angular/core';
import { CommonDataService } from '../common-data.service';
import { CommonMetaDataService } from '../common-meta-data.service';
import { CommonFunctionsService } from '../common-functions.service';
import {
  SQWebProject,
  SQWebArea,
  SQWebSystem,
  LaborLevelwiseData,
  MaterialLevelwiseData,
  MiscLevelwiseData,
  LaborData,
  Material,
  OtPwrSalesOfficeCollection,
  OverTimePWRAdjustment,
  Task,
  Condition,
  SalesOfficeDetails,
  SiteInformation,
  MiscData,
  LaborRates,
  SalesOfficeLaborRates,
  SystemComponent,
  NewCostReviewAttributes_SOL,
  CostReviewType,
  CostByPartNumber,
  HoursByLevel,
  LaborByLevel,
  LaborHoursDistribution,
  SubsAndMiscCosts,
  TaskByLevelWithNotes,
  CostReviewPlannedCosts,
  ConditionByLevelWithNotesDetails,
  TypicalOfSystemHelper
} from '../../models/dtos';
import { CONSTANTS } from '../../Constants/Constants';
import { IndexDBService } from '../common/indexDB.service';

interface LaborHoursByOffice {
  pm: number; eng: number; grap: number; prog: number;
  spec: number; dds: number; mech: number; elec: number;
  neng: number; qeng: number; ceng: number; eeng: number; leng: number;
}

@Injectable({
  providedIn: 'root'
})
export class NewCostReviewPopulateService {

  private static readonly RISK_MISC_REF = 'M0007';

  private sqWebProject: SQWebProject;
  private laborLevelwiseData: LaborLevelwiseData;
  private materialLevelwiseData: MaterialLevelwiseData;
  private miscLevelwiseData: MiscLevelwiseData;
  private readonly salesOfficeNameById: Map<number, string> = new Map();
  private typicalOfHelpers: Record<string, TypicalOfSystemHelper> = {};
  private readonly processedTypicalSystemIds: Map<string, Set<string>> = new Map();

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly indexDbService: IndexDBService
  ) { }

  async populateNewCostReviewAttributes(): Promise<NewCostReviewAttributes_SOL> {
    this.sqWebProject = this.commonDataService.getSQWebProject();
    this.laborLevelwiseData = await this.indexDbService.getAllLaborData();
    this.materialLevelwiseData = await this.indexDbService.getAllMaterialData();
    this.miscLevelwiseData = await this.indexDbService.getAllMiscData();
    this.processedTypicalSystemIds.clear();
    this.buildSalesOfficeNameMap();

    const result = this.createInitializedCostReviewAttributes();

    this.populateBaseAttributes(result);
    this.populateLaborHoursDistribution(result);
    this.populateCostByPartNumber(result);
    this.populateSubsAndMiscCosts(result);
    this.populateLaborByLevel(result);
    this.populateTaskByLevelWithNotes(result);
    this.populateConditionByLevelWithNotes(result);
    this.populateHoursByLevel(result);
    this.populateCostElementsZTC(result);
    this.populateAutoRisk(result);

    return result;
  }

  // ========== Initialization ==========

  private createInitializedCostReviewAttributes(): NewCostReviewAttributes_SOL {
    const result = new NewCostReviewAttributes_SOL();

    // Base class properties
    result.costReviewType = CostReviewType.None;
    result.estimateName = '';
    result.accountName = '';
    result.shipToAddress = '';
    result.sellPrice = 0;
    result.tdc = 0;
    result.gm = 0;
    result.proposalLink = '';
    result.opportunityLink = '';

    // SOL-specific properties
    result.laborHoursDistributionDetails = [];
    result.costByPartNumberDetails = [];
    result.subsAndMiscCostsDetails = [];
    result.laborByLevelDetails = [];
    result.taskByLevelWithNotesDetails = [];
    result.hoursByLevelDetails = [];
    result.costElementsZTC = [];
    result.autoRiskDollars = 0;
    result.autoRiskPercentageOfTDC = 0;
    result.conditionByLevelWithNotesDetails = [];

    return result;
  }

  // ========== Base Attributes ==========

  private populateBaseAttributes(result: NewCostReviewAttributes_SOL): void {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
    const otherMetaData = this.commonDataService.getOtherMetaData();
    const customerInfo = otherMetaData?.customerInformation;
    const siteInfo = otherMetaData?.siteInformation;

    result.costReviewType = this.resolveCostReviewType();
    result.estimateName = this.sqWebProject?.itemName || '';
    result.accountName = customerInfo?.customerName || '';
    result.shipToAddress = this.buildShipToAddress(siteInfo);
    result.sellPrice = estimateTab?.subTotalSellprice || 0;
    result.tdc = estimateTab?.subTotalsCost || 0;
    result.gm = otherMetaData?.grossMargin?.subTotalGMPercent || 0;
    result.proposalLink = this.commonDataService.getEstimateData()?.proposalLink || '';
    result.opportunityLink = this.commonDataService.getOpportunityRelationshipData()?.opportunityLink || '';
  }

  private resolveCostReviewType(): CostReviewType {
    switch (this.commonDataService.getBusiness()) {
      case CONSTANTS.BUSINESS_SECURITY:
        return CostReviewType.SecurityEnUs;
      case CONSTANTS.BUSINESS_FIRE:
        return CostReviewType.FireEnUs;
      case CONSTANTS.BUSINESS_ELECTRICAL:
        return CostReviewType.ElectricalEnUs;
      case CONSTANTS.BUSINESS_MECHANICAL:
        return CostReviewType.MechanicalEnUs;
      default:
        return CostReviewType.AutomationEnUs;
    }
  }

  private buildShipToAddress(siteInfo: SiteInformation): string {
    if (!siteInfo) return '';
    const parts = [
      siteInfo.siteAdd1,
      siteInfo.siteAdd2,
      siteInfo.siteCity,
      siteInfo.siteState,
      siteInfo.siteZipcode
    ].filter(p => p?.trim());
    return parts.join(' ');
  }

  // ========== Sales Office Helpers ==========

  private buildSalesOfficeNameMap(): void {
    this.salesOfficeNameById.clear();
    const salesOffices: SalesOfficeDetails[] = this.commonMetaDataService.getMetaData()?.salesOfficesDetails || [];
    for (const office of salesOffices) {
      this.salesOfficeNameById.set(office.salesOfficeId, office.salesOfficeName);
    }
  }

  private getSalesOfficeName(officeId: number): string {
    return this.salesOfficeNameById.get(officeId) || `Office ${officeId}`;
  }

  // ========== Typical-Of Helpers ==========

  private resolveSystemLaborDataId(system: SQWebSystem): string {
    return this.commonFunctionsService.getCollectionHolderId(system);
  }

  private getTypicalOfCount(system: SQWebSystem): number {
    return system.systemTypicalOfHelperUniqueId
      ? this.commonFunctionsService.getSystemTypicalOfCount(system)
      : 0;
  }

  private getTypicalOfCountInArea(system: SQWebSystem, areaId: string): number {
    return system.systemTypicalOfHelperUniqueId
      ? this.commonFunctionsService.getSystemTypicalOfCountInArea(system, areaId)
      : 0;
  }

  private shouldSkipSystem(system: SQWebSystem, areaId: string): boolean {
    const typicalId = system.systemTypicalOfHelperUniqueId;
    if (!typicalId) return false;
    if (!this.processedTypicalSystemIds.has(areaId)) {
      this.processedTypicalSystemIds.set(areaId, new Set());
    }
    const areaSet = this.processedTypicalSystemIds.get(areaId);
    if (areaSet.has(typicalId)) return true;
    areaSet.add(typicalId);
    return false;
  }

  private replaceNegativeInfiniteNine(value: number): number {
    return CONSTANTS.SENTINEL_LABOR_VALUES.includes(value) ? 0 : value;
  }

  // ========== Hierarchy Traversal ==========

  private forEachLevel(
    callback: (levelId: string, levelDescription: string, nodeType: string, system?: SQWebSystem, areaId?: string) => void
  ): void {
    // Project level
    callback(this.sqWebProject.itemUniqueId, this.sqWebProject.itemName, CONSTANTS.NODETYPE_PROJECT);

    // Areas and their children
    for (const area of this.sqWebProject.areas) {
      callback(area.itemUniqueId, area.itemName, CONSTANTS.NODETYPE_AREA);
      this.processAreaChildren(area, callback);
    }
  }

  private processAreaChildren(
    area: SQWebArea,
    callback: (levelId: string, levelDescription: string, nodeType: string, system?: SQWebSystem, areaId?: string) => void
  ): void {
    if (!area.children) {
      return;
    }
    for (const child of area.children) {
      switch (child.nodeType) {
        case CONSTANTS.NODETYPE_BSETSYSTEM: {
          const system = child as SQWebSystem;
          const laborDataId = this.resolveSystemLaborDataId(system);
          if (!this.shouldSkipSystem(system, area.itemUniqueId)) {
            callback(laborDataId, system.itemName, CONSTANTS.NODETYPE_BSETSYSTEM, system, area.itemUniqueId);
          }
          break;
        }
        case CONSTANTS.NODETYPE_AREA: {
          const nestedArea = child as SQWebArea;
          callback(nestedArea.itemUniqueId, nestedArea.itemName, CONSTANTS.NODETYPE_AREA);
          this.processAreaChildren(nestedArea, callback);
          break;
        }
      }
    }
  }

  // ========== 1. Labor Hours Distribution ==========

  private populateLaborHoursDistribution(result: NewCostReviewAttributes_SOL): void {
    this.processedTypicalSystemIds.clear();
    const rawRows: LaborHoursDistribution[] = [];
    this.forEachLevel((levelId, levelDescription, nodeType, system, areaId) => {
      const laborData = this.laborLevelwiseData?.laborsCollection?.[levelId];
      if (!laborData) {
        return;
      }

      const otCollection: OtPwrSalesOfficeCollection = this.commonDataService.globalPPMmode
        ? laborData.ppmSalesOfficesOtPwrAdjustmentsCollection
        : laborData.stdSalesOfficesOtPwrAdjustmentsCollection;

      if (!otCollection) {
        return;
      }

      const typicalOfCount = system ? (this.getTypicalOfCountInArea(system, areaId) || 1) : 1;

      for (const otPwrSalesOffices of otCollection) {
        for (const otPWRRow of otPwrSalesOffices.overTimePWRAdjustments) {
          const row = new LaborHoursDistribution();
          row.levelID = levelId;
          row.levelDescription = levelDescription;
          row.description = otPWRRow.description || '';
          row.pmHours = this.replaceNegativeInfiniteNine(otPWRRow.projectManagementLabor) * typicalOfCount;
          row.engHours = this.replaceNegativeInfiniteNine(otPWRRow.engineerLabor) * typicalOfCount;
          row.grapHours = this.replaceNegativeInfiniteNine(otPWRRow.graphicsLabor) * typicalOfCount;
          row.progHours = this.replaceNegativeInfiniteNine(otPWRRow.programmingLabor) * typicalOfCount;
          row.specHours = this.replaceNegativeInfiniteNine(otPWRRow.specialistLabor) * typicalOfCount;
          row.ddsHours = this.replaceNegativeInfiniteNine(otPWRRow.digitalDeploymentLabor) * typicalOfCount;
          row.mechHours = this.replaceNegativeInfiniteNine(otPWRRow.mechanicalEngineerLabor) * typicalOfCount;
          row.elecHours = this.replaceNegativeInfiniteNine(otPWRRow.electricalEngineerLabor) * typicalOfCount;
          row.typicalOfCount = system ? (this.getTypicalOfCount(system) || 1).toString() : '1';
          row.typicalOfCountInArea = system ? typicalOfCount.toString() : '';
          rawRows.push(row);
        }
      }
    });

    // Consolidate rows by levelID + description (merge across sales offices)
    const consolidatedMap = new Map<string, LaborHoursDistribution>();
    for (const row of rawRows) {
      const key = `${row.levelID}||${row.description}`;
      const existing = consolidatedMap.get(key);
      if (existing) {
        existing.pmHours += row.pmHours;
        existing.engHours += row.engHours;
        existing.grapHours += row.grapHours;
        existing.progHours += row.progHours;
        existing.specHours += row.specHours;
        existing.ddsHours += row.ddsHours;
        existing.mechHours += row.mechHours;
        existing.elecHours += row.elecHours;
      } else {
        consolidatedMap.set(key, row);
      }
    }
    result.laborHoursDistributionDetails = Array.from(consolidatedMap.values());
  }

  // ========== 2. Cost By Part Number ==========

  private populateCostByPartNumber(result: NewCostReviewAttributes_SOL): void {
    if (!this.materialLevelwiseData?.materialsCollection) return;

    const rawRows: CostByPartNumber[] = [];
    for (const materialData of Object.values(this.materialLevelwiseData.materialsCollection)) {
      if (!materialData?.systemChildrenCollection) continue;
      this.processMaterialChildren(materialData.systemChildrenCollection, rawRows);
    }

    // Consolidate rows by referenceNumber
    const consolidatedMap = new Map<string, CostByPartNumber>();
    for (const row of rawRows) {
      const key = row.referenceNumber;
      const existing = consolidatedMap.get(key);
      if (existing) {
        existing.quantity += row.quantity;
        existing.cost += row.cost;
        existing.pmAdjustedHours += row.pmAdjustedHours;
        existing.engAdjustedHours += row.engAdjustedHours;
        existing.specAdjustedHours += row.specAdjustedHours;
        existing.mechAdjustedHours += row.mechAdjustedHours;
        existing.elecAdjustedHours += row.elecAdjustedHours;
        existing.nengEngineerAdjustedHrs += row.nengEngineerAdjustedHrs;
        existing.qengEngineerAdjustedHrs += row.qengEngineerAdjustedHrs;
        existing.cengEngineerAdjustedHrs += row.cengEngineerAdjustedHrs;
        existing.eengEngineerAdjustedHrs += row.eengEngineerAdjustedHrs;
        existing.lengEngineerAdjustedHrs += row.lengEngineerAdjustedHrs;
        existing.programmingAdjustedHrs += row.programmingAdjustedHrs;
        existing.graphicsAdjustedHrs += row.graphicsAdjustedHrs;
        existing.ddsAdjustedHrs += row.ddsAdjustedHrs;
        // Recompute unitCost from consolidated totals
        existing.unitCost = existing.quantity > 0 ? existing.cost / existing.quantity : 0;
        // Remove if both unitCost and cost are zero after consolidation
        if (existing.unitCost === 0 && existing.cost === 0) {
          consolidatedMap.delete(key);
        }
      }
      else if (row.unitCost !== 0 || row.cost !== 0) {
        consolidatedMap.set(key, row);
      }
    }
    result.costByPartNumberDetails = Array.from(consolidatedMap.values());
  }

  private processMaterialChildren(children: SystemComponent[], rows: CostByPartNumber[]): void {
    for (const item of children) {
      if (item.systemComponentNodeType !== CONSTANTS.NODETYPE_MATERIAL) continue;
      const material = item as Material;
      if (material.quantity <= 0) continue;

      const row = new CostByPartNumber();
      row.section = material.isRPN ? CONSTANTS.RPN_INDICATOR : CONSTANTS.SSN_INDICATOR;
      row.referenceNumber = material.referenceNumber || '';
      row.description = material.description || '';
      row.quantity = material.quantity;
      row.unitCost = material.cost > 0 && material.quantity > 0
        ? material.cost / material.quantity
        : 0;
      row.cost = material.cost || 0;
      row.pmAdjustedHours = this.replaceNegativeInfiniteNine(material.projectManagementLabor);
      row.engAdjustedHours = this.replaceNegativeInfiniteNine(material.engineerLabor);
      row.specAdjustedHours = this.replaceNegativeInfiniteNine(material.specialistLabor);
      row.mechAdjustedHours = this.replaceNegativeInfiniteNine(material.mechanicalEngineerLabor);
      row.elecAdjustedHours = this.replaceNegativeInfiniteNine(material.electricalEngineerLabor);
      row.nengEngineerAdjustedHrs = this.replaceNegativeInfiniteNine(material.networkEngineerLabor);
      row.qengEngineerAdjustedHrs = this.replaceNegativeInfiniteNine(material.qualificationEngineerLabor);
      row.cengEngineerAdjustedHrs = this.replaceNegativeInfiniteNine(material.cyberSecEngineerLabor);
      row.eengEngineerAdjustedHrs = this.replaceNegativeInfiniteNine(material.energyEngineerLabor);
      row.lengEngineerAdjustedHrs = this.replaceNegativeInfiniteNine(material.lengEngineerLabor);
      row.programmingAdjustedHrs = this.replaceNegativeInfiniteNine(material.programmingLabor);
      row.graphicsAdjustedHrs = this.replaceNegativeInfiniteNine(material.graphicsLabor);
      row.ddsAdjustedHrs = this.replaceNegativeInfiniteNine(material.digitalDeploymentLabor);
      row.notes = material.laborTabNotes || '';
      row.modelNumber = material.modelNumber || '';
      row.manufacturerPartNumber = material.mpn || '';
      rows.push(row);
    }
  }

  // ========== 3. Subs And Misc Costs ==========

  private populateSubsAndMiscCosts(result: NewCostReviewAttributes_SOL): void {
    this.processedTypicalSystemIds.clear();

    this.forEachLevel((levelId, levelDescription, nodeType, system, areaId) => {
      const miscData = this.miscLevelwiseData?.miscsCollection?.[levelId];
      if (!miscData) return;

      const isSystemLevel = nodeType === CONSTANTS.NODETYPE_BSETSYSTEM;
      const typicalOfCountInArea = system ? this.getTypicalOfCountInArea(system, areaId).toString() : '0';

      this.collectSubcontractorRows(miscData, levelId, levelDescription, typicalOfCountInArea, isSystemLevel, result);
      this.collectMiscellaneousRows(miscData, levelId, levelDescription, typicalOfCountInArea, isSystemLevel, result);
    });

    // Add project-level Auto Risk and Technology Fee entries
    this.addProjectLevelMiscCosts(result);
  }

  private addProjectLevelMiscCosts(result: NewCostReviewAttributes_SOL): void {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
    if (!estimateTab) return;

    const projectLevelId = this.sqWebProject.itemUniqueId;
    const projectLevelDescription = this.sqWebProject.itemName;

    const autoRiskCost = estimateTab.autoRiskCost || 0;
    if (autoRiskCost > 0) {
      const autoRiskRow = new SubsAndMiscCosts();
      autoRiskRow.levelID = projectLevelId;
      autoRiskRow.levelDescription = projectLevelDescription;
      autoRiskRow.description = CONSTANTS.AUTO_RISK_LABEL;
      autoRiskRow.subcontractorCost = 0;
      autoRiskRow.miscellaneousCost = autoRiskCost;
      autoRiskRow.typicalOfCountInArea = '0';
      autoRiskRow.isSystemLevel = false;
      autoRiskRow.notes = '';
      autoRiskRow.isRiskItem = false;
      result.subsAndMiscCostsDetails.push(autoRiskRow);
    }

    const technologyFeeCost = estimateTab.technologyFeeCost || 0;
    if (technologyFeeCost > 0) {
      const techFeeRow = new SubsAndMiscCosts();
      techFeeRow.levelID = projectLevelId;
      techFeeRow.levelDescription = projectLevelDescription;
      techFeeRow.description = CONSTANTS.TECHNOLOGY_FEE_LABEL;
      techFeeRow.subcontractorCost = 0;
      techFeeRow.miscellaneousCost = technologyFeeCost;
      techFeeRow.typicalOfCountInArea = '0';
      techFeeRow.isSystemLevel = false;
      techFeeRow.notes = '';
      techFeeRow.isRiskItem = false;
      result.subsAndMiscCostsDetails.push(techFeeRow);
    }
  }

  private collectSubcontractorRows(
    miscData: MiscData, levelId: string, levelDescription: string,
    typicalOfCountInArea: string, isSystemLevel: boolean, result: NewCostReviewAttributes_SOL
  ): void {
    if (!miscData.subcontractorCollection) return;
    for (const sub of miscData.subcontractorCollection) {
      if (sub.cost <= 0) continue;
      const row = new SubsAndMiscCosts();
      row.levelID = levelId;
      row.levelDescription = levelDescription;
      row.description = `${sub.referenceNumber} ${sub.description}`;
      row.subcontractorCost = sub.cost;
      row.miscellaneousCost = 0;
      row.typicalOfCountInArea = typicalOfCountInArea;
      row.isSystemLevel = isSystemLevel;
      row.notes = sub.notes || '';
      row.isRiskItem = false;
      result.subsAndMiscCostsDetails.push(row);
    }
  }

  private collectMiscellaneousRows(
    miscData: MiscData, levelId: string, levelDescription: string,
    typicalOfCountInArea: string, isSystemLevel: boolean, result: NewCostReviewAttributes_SOL
  ): void {
    if (!miscData.miscellaneousCollection) return;
    for (const misc of miscData.miscellaneousCollection) {
      if (misc.cost <= 0) continue;
      const row = new SubsAndMiscCosts();
      row.levelID = levelId;
      row.levelDescription = levelDescription;
      row.description = `${misc.referenceNumber} ${misc.description}`;
      row.subcontractorCost = 0;
      row.miscellaneousCost = misc.cost;
      row.typicalOfCountInArea = typicalOfCountInArea;
      row.isSystemLevel = isSystemLevel;
      row.notes = misc.notes || '';
      row.isRiskItem = this.isRiskMiscItem(misc.referenceNumber);
      result.subsAndMiscCostsDetails.push(row);
    }
  }

  private isRiskMiscItem(referenceNumber: string): boolean {
    return referenceNumber?.toUpperCase().includes(NewCostReviewPopulateService.RISK_MISC_REF) || false;
  }

  // ========== 4. Labor By Level ==========

  private populateLaborByLevel(result: NewCostReviewAttributes_SOL): void {
    this.processedTypicalSystemIds.clear();
    const laborRates: SalesOfficeLaborRates | undefined = this.commonDataService.getLaborMetaData()?.salesOfficeLaborRates;
    let levelIndex = 0;

    this.forEachLevel((levelId, levelDescription, nodeType, system, areaId) => {
      levelIndex++;
      const laborData = this.laborLevelwiseData?.laborsCollection?.[levelId];
      if (!laborData) return;

      const typicalOfCount = system ? this.getTypicalOfCount(system) : 0;
      const difficultyLevel = system ? this.getSystemDifficultyLevel(system) : '';

      const officeHoursMap = this.aggregateHoursByOffice(laborData);
      this.createLaborByLevelRows(officeHoursMap, laborRates, laborData, { levelIndex, levelDescription, typicalOfCount, difficultyLevel }, result);
    });

    // Add Summary rows - aggregate all level rows by laborType
    this.addLaborByLevelSummaryRows(result);
  }

  private addLaborByLevelSummaryRows(result: NewCostReviewAttributes_SOL): void {
    const summaryMap = new Map<string, LaborByLevel>();
    for (const row of result.laborByLevelDetails) {
      const key = row.laborType;
      const existing = summaryMap.get(key);
      if (existing) {
        existing.baseHours += row.baseHours;
        existing.adjustmentHours += row.adjustmentHours;
        existing.hoursTotal += row.hoursTotal;
        existing.costTotal += row.costTotal;
      } else {
        const summaryRow = new LaborByLevel();
        summaryRow.levelID = 0;
        summaryRow.levelDescription = CONSTANTS.SUMMARY_LABEL;
        summaryRow.laborType = row.laborType;
        summaryRow.servicingOffice = row.servicingOffice;
        summaryRow.standardRate = row.standardRate;
        summaryRow.baseHours = row.baseHours;
        summaryRow.adjustmentHours = row.adjustmentHours;
        summaryRow.hoursTotal = row.hoursTotal;
        summaryRow.otpwr = row.otpwr;
        summaryRow.costTotal = row.costTotal;
        summaryRow.typicalOfCount = '';
        summaryRow.systemDifficultyLevel = '';
        summaryMap.set(key, summaryRow);
      }
    }
    for (const summaryRow of summaryMap.values()) {
      summaryRow.otpwr = summaryRow.adjustmentHours === 0 ? 'No' : 'Yes';
      result.laborByLevelDetails.push(summaryRow);
    }
  }

  private aggregateHoursByOffice(laborData: LaborData): Map<number, LaborHoursByOffice> {
    const allItems = [
      ...(laborData.baseTasksCollection || []),
      ...(laborData.tasksCollection || []),
      ...(laborData.conditionsCollection || [])
    ];

    const officeHoursMap = new Map<number, LaborHoursByOffice>();

    for (const task of allItems) {
      const officeId = task.salesOfficeId;
      const hours = officeHoursMap.get(officeId) || {
        pm: 0, eng: 0, grap: 0, prog: 0, spec: 0, dds: 0,
        mech: 0, elec: 0, neng: 0, qeng: 0, ceng: 0, eeng: 0, leng: 0
      };
      hours.pm += this.replaceNegativeInfiniteNine(task.projectManagementLabor);
      hours.eng += this.replaceNegativeInfiniteNine(task.engineerLabor);
      hours.grap += this.replaceNegativeInfiniteNine(task.graphicsLabor);
      hours.prog += this.replaceNegativeInfiniteNine(task.programmingLabor);
      hours.spec += this.replaceNegativeInfiniteNine(task.specialistLabor);
      hours.dds += this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor);
      hours.mech += this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor);
      hours.elec += this.replaceNegativeInfiniteNine(task.electricalEngineerLabor);
      hours.neng += this.replaceNegativeInfiniteNine(task.networkEngineerLabor);
      hours.qeng += this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor);
      hours.ceng += this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor);
      hours.eeng += this.replaceNegativeInfiniteNine(task.energyEngineerLabor);
      hours.leng += this.replaceNegativeInfiniteNine(task.lengEngineerLabor);
      officeHoursMap.set(officeId, hours);
    }

    return officeHoursMap;
  }

  private static readonly LABOR_TYPES: { key: string; label: string }[] = [
    { key: 'pm', label: 'PM' },
    { key: 'eng', label: 'ENG' },
    { key: 'neng', label: 'NENG' },
    { key: 'qeng', label: 'QENG' },
    { key: 'ceng', label: 'CENG' },
    { key: 'eeng', label: 'EENG' },
    { key: 'leng', label: 'LENG' },
    { key: 'grap', label: 'Grap' },
    { key: 'prog', label: 'Prog' },
    { key: 'spec', label: 'SPEC' },
    { key: 'dds', label: 'Ddep' },
    { key: 'mech', label: 'Mech' },
    { key: 'elec', label: 'Elec' },
  ];

  private createLaborByLevelRows(
    officeHoursMap: Map<number, LaborHoursByOffice>,
    laborRates: SalesOfficeLaborRates | undefined,
    laborData: LaborData,
    ctx: { levelIndex: number; levelDescription: string; typicalOfCount: number; difficultyLevel: string },
    result: NewCostReviewAttributes_SOL
  ): void {
    for (const [officeId, hours] of officeHoursMap.entries()) {
      const rate = laborRates?.find((r: LaborRates) => r.salesOfficeId === officeId);
      const officeName = this.getSalesOfficeName(officeId);

      for (const lt of NewCostReviewPopulateService.LABOR_TYPES) {
        const baseHours = hours[lt.key as keyof LaborHoursByOffice] || 0;
        if (baseHours === 0) continue;

        const standardRate = this.getStandardRate(rate, lt.key);
        const adjustmentHours = this.getOtPwrAdjustmentHours(laborData, officeId, lt.key);
        const hoursTotal = baseHours + adjustmentHours;
        const costTotal = hoursTotal * standardRate;

        const row = new LaborByLevel();
        row.levelID = ctx.levelIndex;
        row.levelDescription = ctx.levelDescription;
        row.laborType = lt.label;
        row.servicingOffice = officeName;
        row.standardRate = standardRate;
        row.baseHours = baseHours;
        row.adjustmentHours = adjustmentHours;
        row.hoursTotal = hoursTotal;
        row.otpwr = adjustmentHours === 0 ? 'No' : 'Yes';
        row.costTotal = costTotal;
        row.typicalOfCount = ctx.typicalOfCount > 0 ? ctx.typicalOfCount.toString() : '';
        row.systemDifficultyLevel = ctx.difficultyLevel;
        result.laborByLevelDetails.push(row);
      }
    }
  }

  private getStandardRate(rate: LaborRates | undefined, laborType: string): number {
    if (!rate) return 0;
    const rateMap: Record<string, keyof LaborRates> = {
      'pm': 'projectManagementRates',
      'eng': 'engineeringRates',
      'neng': 'networkEngineeringRates',
      'qeng': 'qualificationEngineeringRates',
      'ceng': 'cyberSecEngineeringRates',
      'eeng': 'energyEngineeringRates',
      'leng': 'lEngineeringRates',
      'grap': 'graphicsRates',
      'prog': 'programmingRates',
      'spec': 'specialistRates',
      'dds': 'ddsRates',
      'mech': 'mechanicalRates',
      'elec': 'electricianRates',
    };
    return (rate[rateMap[laborType]] as number) || 0;
  }

  private getOtPwrAdjustmentHours(laborData: LaborData, officeId: number, laborType: string): number {
    const otCollection = this.commonDataService.globalPPMmode
      ? laborData.ppmSalesOfficesOtPwrAdjustmentsCollection
      : laborData.stdSalesOfficesOtPwrAdjustmentsCollection;

    if (!otCollection) return 0;

    let adjustmentHours = 0;
    for (const office of otCollection) {
      if (office.salesOfficeId !== officeId) continue;
      for (const adj of office.overTimePWRAdjustments) {
        if (adj.description === CONSTANTS.NORMAL_HOURS_RATE) continue;
        adjustmentHours += this.getOtPwrHoursByLaborType(adj, laborType);
      }
    }
    return adjustmentHours;
  }

  private getOtPwrHoursByLaborType(adj: OverTimePWRAdjustment, laborType: string): number {
    const fieldMap: Record<string, string> = {
      'pm': 'projectManagementLabor',
      'eng': 'engineerLabor',
      'neng': 'networkEngineerLabor',
      'qeng': 'qualificationEngineerLabor',
      'ceng': 'cyberSecEngineerLabor',
      'eeng': 'energyEngineerLabor',
      'leng': 'lengEngineerLabor',
      'grap': 'graphicsLabor',
      'prog': 'programmingLabor',
      'spec': 'specialistLabor',
      'dds': 'digitalDeploymentLabor',
      'mech': 'mechanicalEngineerLabor',
      'elec': 'electricalEngineerLabor',
    };
    return this.replaceNegativeInfiniteNine((adj[fieldMap[laborType] as keyof OverTimePWRAdjustment] as number) || 0);
  }

  private getSystemDifficultyLevel(system: SQWebSystem): string {
    switch (system.difficultyId) {
      case 1: return 'Simple';
      case 2: return 'Standard';
      default: return system.difficultyId >= 3 ? 'Complex' : '';
    }
  }

  // ========== 5. Task By Level With Notes ==========

  private populateTaskByLevelWithNotes(result: NewCostReviewAttributes_SOL): void {
    this.processedTypicalSystemIds.clear();
    const processedTypicalIdsForTasks = new Set<string>();

    this.forEachLevel((levelId, levelDescription, nodeType, system, areaId) => {
      // Deduplicate typical systems globally for task report
      if (system) {
        const typicalId = system.systemTypicalOfHelperUniqueId;
        if (typicalId) {
          if (processedTypicalIdsForTasks.has(typicalId)) return;
          processedTypicalIdsForTasks.add(typicalId);
        }
      }

      const laborData = this.laborLevelwiseData?.laborsCollection?.[levelId];
      if (!laborData) return;

      const allTasks: Task[] = [
        ...(laborData.baseTasksCollection || []),
        ...(laborData.tasksCollection || [])
      ];

      const typicalOfCount = system ? (this.getTypicalOfCount(system) || 1) : 1;

      for (const task of allTasks) {
        const pm = this.replaceNegativeInfiniteNine(task.projectManagementLabor) * typicalOfCount;
        const eng = this.replaceNegativeInfiniteNine(task.engineerLabor) * typicalOfCount;
        const grap = this.replaceNegativeInfiniteNine(task.graphicsLabor) * typicalOfCount;
        const prog = this.replaceNegativeInfiniteNine(task.programmingLabor) * typicalOfCount;
        const spec = this.replaceNegativeInfiniteNine(task.specialistLabor) * typicalOfCount;
        const dds = this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor) * typicalOfCount;
        const mech = this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor) * typicalOfCount;
        const elec = this.replaceNegativeInfiniteNine(task.electricalEngineerLabor) * typicalOfCount;

        if (pm === 0 && eng === 0 && grap === 0 && prog === 0 && spec === 0 &&
          dds === 0 && mech === 0 && elec === 0) {
          continue;
        }

        const row = new TaskByLevelWithNotes();
        row.levelID = levelId;
        row.levelDescription = levelDescription;
        row.taskID = task.referenceNumber || '';
        row.taskDescription = task.description || '';
        row.pmHours = pm;
        row.engHours = eng;
        row.grapHours = grap;
        row.progHours = prog;
        row.specHours = spec;
        row.ddsHours = dds;
        row.mechHours = mech;
        row.elecHours = elec;
        row.servicingOffice = this.getSalesOfficeName(task.salesOfficeId);
        row.notes = task.laborTabNotes || '';
        row.typicalOfCount = typicalOfCount > 1 ? typicalOfCount.toString() : '';
        result.taskByLevelWithNotesDetails.push(row);
      }
    });

    // Add Summary rows - aggregate all tasks by taskID across levels
    this.addTaskByLevelSummaryRows(result);
  }

  private addTaskByLevelSummaryRows(result: NewCostReviewAttributes_SOL): void {
    const summaryMap = new Map<string, TaskByLevelWithNotes>();
    for (const row of result.taskByLevelWithNotesDetails) {
      const key = row.taskID;
      const existing = summaryMap.get(key);
      if (existing) {
        existing.pmHours += row.pmHours;
        existing.engHours += row.engHours;
        existing.grapHours += row.grapHours;
        existing.progHours += row.progHours;
        existing.specHours += row.specHours;
        existing.ddsHours += row.ddsHours;
        existing.mechHours += row.mechHours;
        existing.elecHours += row.elecHours;
      } else {
        const summaryRow = new TaskByLevelWithNotes();
        summaryRow.levelID = this.sqWebProject.itemUniqueId;
        summaryRow.levelDescription = CONSTANTS.SUMMARY_LABEL;
        summaryRow.taskID = row.taskID;
        summaryRow.taskDescription = row.taskDescription;
        summaryRow.pmHours = row.pmHours;
        summaryRow.engHours = row.engHours;
        summaryRow.grapHours = row.grapHours;
        summaryRow.progHours = row.progHours;
        summaryRow.specHours = row.specHours;
        summaryRow.ddsHours = row.ddsHours;
        summaryRow.mechHours = row.mechHours;
        summaryRow.elecHours = row.elecHours;
        summaryRow.servicingOffice = row.servicingOffice;
        summaryRow.notes = row.notes;
        summaryRow.typicalOfCount = '';
        summaryMap.set(key, summaryRow);
      }
    }
    for (const summaryRow of summaryMap.values()) {
      result.taskByLevelWithNotesDetails.unshift(summaryRow);
    }
  }

  // ========== 6. Condition By Level With Notes ==========

  private populateConditionByLevelWithNotes(result: NewCostReviewAttributes_SOL): void {
    this.processedTypicalSystemIds.clear();
    const processedTypicalIdsForConditions = new Set<string>();

    this.forEachLevel((levelId, levelDescription, nodeType, system) => {
      // Global dedup for typical systems in condition report
      if (system) {
        const typicalId = system.systemTypicalOfHelperUniqueId;
        if (typicalId) {
          if (processedTypicalIdsForConditions.has(typicalId)) return;
          processedTypicalIdsForConditions.add(typicalId);
        }
      }

      const laborData = this.laborLevelwiseData?.laborsCollection?.[levelId];
      if (!laborData?.conditionsCollection) return;

      const typicalOfCount = system ? (this.getTypicalOfCount(system) || 1) : 1;

      for (const condition of laborData.conditionsCollection) {
        const pm = this.replaceNegativeInfiniteNine(condition.projectManagementLabor) * typicalOfCount;
        const eng = this.replaceNegativeInfiniteNine(condition.engineerLabor) * typicalOfCount;
        const grap = this.replaceNegativeInfiniteNine(condition.graphicsLabor) * typicalOfCount;
        const prog = this.replaceNegativeInfiniteNine(condition.programmingLabor) * typicalOfCount;
        const spec = this.replaceNegativeInfiniteNine(condition.specialistLabor) * typicalOfCount;
        const dds = this.replaceNegativeInfiniteNine(condition.digitalDeploymentLabor) * typicalOfCount;
        const mech = this.replaceNegativeInfiniteNine(condition.mechanicalEngineerLabor) * typicalOfCount;
        const elec = this.replaceNegativeInfiniteNine(condition.electricalEngineerLabor) * typicalOfCount;

        if (pm === 0 && eng === 0 && grap === 0 && prog === 0 &&
          spec === 0 && dds === 0 && mech === 0 && elec === 0) {
          continue;
        }

        const row = new ConditionByLevelWithNotesDetails();
        row.levelID = levelId;
        row.levelDescription = levelDescription;
        row.conditionID = condition.referenceNumber || '';
        row.conditionDescription = condition.description || '';
        row.pmHours = pm;
        row.engHours = eng;
        row.grapHours = grap;
        row.progHours = prog;
        row.specHours = spec;
        row.ddsHours = dds;
        row.mechHours = mech;
        row.elecHours = elec;
        row.servicingOffice = this.getSalesOfficeName(condition.salesOfficeId);
        row.notes = condition.laborTabNotes || '';
        row.typicalOfCount = typicalOfCount > 1 ? typicalOfCount.toString() : '';
        result.conditionByLevelWithNotesDetails.push(row);
      }
    });
  }

  // ========== 7. Hours By Level ==========

  private populateHoursByLevel(result: NewCostReviewAttributes_SOL): void {
    this.processedTypicalSystemIds.clear();

    this.forEachLevel((levelId, levelDescription, nodeType, system, areaId) => {
      const laborData = this.laborLevelwiseData?.laborsCollection?.[levelId];

      const allTasks: Task[] = laborData ? [
        ...(laborData.baseTasksCollection || []),
        ...(laborData.tasksCollection || [])
      ] : [];
      const allConditions: Condition[] = laborData ? [...(laborData.conditionsCollection || [])] : [];

      const typicalOfCount = system ? (this.getTypicalOfCount(system) || 1) : 1;
      const multiplier = typicalOfCount;

      let pm = 0, eng = 0, grap = 0, prog = 0, spec = 0, dds = 0, mech = 0, elec = 0;
      let neng = 0, qeng = 0, ceng = 0, eeng = 0, leng = 0;

      for (const task of [...allTasks, ...allConditions]) {
        pm += this.replaceNegativeInfiniteNine(task.projectManagementLabor);
        eng += this.replaceNegativeInfiniteNine(task.engineerLabor);
        grap += this.replaceNegativeInfiniteNine(task.graphicsLabor);
        prog += this.replaceNegativeInfiniteNine(task.programmingLabor);
        spec += this.replaceNegativeInfiniteNine(task.specialistLabor);
        dds += this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor);
        mech += this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor);
        elec += this.replaceNegativeInfiniteNine(task.electricalEngineerLabor);
        neng += this.replaceNegativeInfiniteNine(task.networkEngineerLabor);
        qeng += this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor);
        ceng += this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor);
        eeng += this.replaceNegativeInfiniteNine(task.energyEngineerLabor);
        leng += this.replaceNegativeInfiniteNine(task.lengEngineerLabor);
      }

      pm *= multiplier;
      eng *= multiplier;
      grap *= multiplier;
      prog *= multiplier;
      spec *= multiplier;
      dds *= multiplier;
      mech *= multiplier;
      elec *= multiplier;
      neng *= multiplier;
      qeng *= multiplier;
      ceng *= multiplier;
      eeng *= multiplier;
      leng *= multiplier;

      const totalHours = pm + eng + grap + prog + spec + dds + mech + elec + neng + qeng + ceng + eeng + leng;
      // Only skip BSETSystem nodes with zero hours; include Areas even with 0 hours
      if (totalHours === 0 && nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) return;

      // Get point counts from system properties
      let hardwarePoints = CONSTANTS.NA;
      let flnPoints = CONSTANTS.NA;
      let softwarePoints = CONSTANTS.NA;
      let totalPoints = CONSTANTS.NA;
      let difficulty = CONSTANTS.NA;

      if (system) {
        hardwarePoints = (system.totalTECZeroPoints || 0).toString();
        flnPoints = (system.totalFLNPoints || 0).toString();
        softwarePoints = (system.totalINTPoints || 0).toString();
        totalPoints = (system.systemTotalPoints || 0).toString();
        difficulty = this.getSystemDifficultyLevel(system);
      }

      // Calculate total cost from labor + material
      const laborCost = this.calculateLevelTotalCost(result.laborByLevelDetails, levelDescription);
      const materialCost = this.calculateLevelMaterialCost(levelId);
      const totalCost = laborCost + materialCost;

      const typicalOfCountStr = this.resolveTypicalOfCountString(typicalOfCount, nodeType);

      const row = new HoursByLevel();
      row.levelId = levelId;
      row.levelDescription = levelDescription;
      row.difficulty = difficulty;
      row.typicalOfCount = typicalOfCountStr;
      row.hardwarePoints = hardwarePoints;
      row.fln = flnPoints;
      row.softwarePoints = softwarePoints;
      row.totalPoints = totalPoints;
      row.pm = pm;
      row.eng = eng;
      row.grap = grap;
      row.prog = prog;
      row.spec = spec;
      row.dds = dds;
      row.mech = mech;
      row.elec = elec;
      row.neng = neng;
      row.qeng = qeng;
      row.ceng = ceng;
      row.eeng = eeng;
      row.leng = leng;
      row.totalHours = totalHours;
      row.totalCost = totalCost;
      row.notes = system?.laborTabNotes || '';
      result.hoursByLevelDetails.push(row);
    });
  }

  private resolveTypicalOfCountString(typicalOfCount: number, nodeType: string): string {
    if (typicalOfCount > 1) return typicalOfCount.toString();
    if (nodeType === CONSTANTS.NODETYPE_PROJECT) return CONSTANTS.NA;
    return '1';
  }

  private calculateLevelMaterialCost(levelId: string): number {
    const materialData = this.materialLevelwiseData?.materialsCollection?.[levelId];
    if (!materialData?.systemChildrenCollection) return 0;

    let total = 0;
    for (const item of materialData.systemChildrenCollection) {
      if (item.systemComponentNodeType !== CONSTANTS.NODETYPE_MATERIAL) continue;
      const material = item as Material;
      total += material.cost || 0;
    }
    return total;
  }

  private calculateLevelTotalCost(laborByLevelDetails: LaborByLevel[], levelDescription: string): number {
    return laborByLevelDetails
      .filter(l => l.levelDescription === levelDescription)
      .reduce((sum, l) => sum + l.costTotal, 0);
  }

  // ========== 8. Cost Elements ZTC ==========

  private populateCostElementsZTC(result: NewCostReviewAttributes_SOL): void {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
    if (!estimateTab) return;

    const tdc = estimateTab.subTotalsCost || 0;

    const costElements: { name: string; cost: number; hours: number }[] = [
      { name: 'ENG', cost: estimateTab.engineeringLaborCost || 0, hours: estimateTab.engineeringLaborHour || 0 },
      { name: 'NENG', cost: estimateTab.nEngineeringLaborCost || 0, hours: estimateTab.networkEngineeringLaborHour || 0 },
      { name: 'QENG', cost: estimateTab.qEngineeringLaborCost || 0, hours: estimateTab.qualificationEngineeringLaborHour || 0 },
      { name: 'CENG', cost: estimateTab.cEngineeringLaborCost || 0, hours: estimateTab.cyberSecEngineeringLaborHour || 0 },
      { name: 'EENG', cost: estimateTab.eEngineeringLaborCost || 0, hours: estimateTab.energyEngineeringLaborHour || 0 },
      { name: 'LENG', cost: estimateTab.lEngineeringLaborCost || 0, hours: estimateTab.lEngineeringLaborHour || 0 },
      { name: 'Prog', cost: estimateTab.programmingLaborCost || 0, hours: estimateTab.programmingLaborHour || 0 },
      { name: 'Grap', cost: estimateTab.graphicsLaborCost || 0, hours: estimateTab.graphicsLaborHour || 0 },
      { name: 'DDS', cost: estimateTab.digitalDeploymentLaborCost || 0, hours: estimateTab.digitalDeploymentLaborHour || 0 },
      { name: 'Spec', cost: estimateTab.specialistLaborCost || 0, hours: estimateTab.specialistLaborHour || 0 },
      { name: 'Mech', cost: estimateTab.mechanicalLaborCost || 0, hours: estimateTab.mechanicalLaborHour || 0 },
      { name: 'Elec', cost: estimateTab.electricianLaborCost || 0, hours: estimateTab.electricianLaborHour || 0 },
    ];

    for (const element of costElements) {
      if (element.cost === 0 && element.hours === 0) continue;

      const row = new CostReviewPlannedCosts();
      row.itemName = element.name;
      row.itemCost = element.cost;
      row.tdcPercent = tdc > 0 ? (element.cost / tdc) * 100 : 0;
      row.target = 0;
      row.riskDisplayText = 0;
      row.targetVisible = false;
      row.nationalHistoricTDCPercent = 0;
      row.positionId = 0;
      row.hours = element.hours;
      result.costElementsZTC.push(row);
    }
  }

  // ========== 9. Auto Risk ==========

  private populateAutoRisk(result: NewCostReviewAttributes_SOL): void {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
    if (!estimateTab) return;

    result.autoRiskDollars = estimateTab.autoRiskCost || 0;
    const tdc = estimateTab.subTotalsCost || 0;
    result.autoRiskPercentageOfTDC = tdc > 0 ? (result.autoRiskDollars / tdc) * 100 : 0;
  }
}